import React from 'react';
import { Card, Divider } from 'antd';
import { Chart } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

/*
 * Throwaway test view to validate chartjs-plugin-annotation@3 against the
 * project's chart.js@4 / react-chartjs-2@5 stack.
 *
 * The plugin is registered GLOBALLY via Chart.register (same pattern the
 * project uses for datalabels/zoom). The per-chart `plugins` prop path causes
 * "Cannot read properties of undefined (reading 'borderCapStyle')" on
 * re-render, because react-chartjs-2 drops the annotation element's resolved
 * options between renders. Global registration avoids that.
 *
 * Note: registering globally makes annotation available to every chart, but it
 * only draws anything when a chart sets options.plugins.annotation — charts
 * that don't (all the existing ones) are unaffected.
 *
 * Reproduces the two annotation use-cases from the Pipeline Health Dashboard:
 *   - p.5 freshness: a red horizontal reference line (expected_frequency_hrs)
 *   - p.6 error timeline: a shaded box marking the incident window
 *
 * Route: /dev/annotation-test  (remove once validated)
 */

Chart.register(annotationPlugin);

// --- p.5: horizontal bars (hours_since_success) + red SLA reference line ---
const slaData = {
  labels: ['pattern', 'pattern_stop', 'geotab_device', 'geotab_trip', 'fleet'],
  datasets: [
    {
      label: 'hours_since_success',
      data: [12, 12, 1, 1, 12],
      backgroundColor: '#52c41a',
    },
  ],
};

const slaOptions = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { beginAtZero: true, title: { display: true, text: 'hours' } },
  },
  plugins: {
    legend: { display: true },
    annotation: {
      annotations: {
        expectedFreq: {
          type: 'line',
          xMin: 24,
          xMax: 24,
          borderColor: '#ff4d4f',
          borderWidth: 2,
          borderDash: [6, 4],
          label: {
            display: true,
            content: 'expected_frequency_hrs (24h)',
            position: 'start',
            backgroundColor: 'rgba(255,77,79,0.85)',
          },
        },
      },
    },
  },
};

// --- p.6: HMS health-score timeline + shaded incident band ---
const incidentData = {
  labels: [
    '2025-05', '2025-06', '2025-07', '2025-08', '2025-09',
    '2025-10', '2025-11', '2025-12', '2026-01',
  ],
  datasets: [
    {
      label: 'HMS health score',
      data: [62, 30, 25, 50, 55, 60, 70, 90, 100],
      borderColor: '#1677ff',
      backgroundColor: 'rgba(22,119,255,0.15)',
      fill: true,
      tension: 0.3,
    },
  ],
};

const incidentOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: { min: 0, max: 100 },
  },
  plugins: {
    legend: { display: true },
    annotation: {
      annotations: {
        incidentBand: {
          type: 'box',
          xMin: '2025-06',
          xMax: '2025-07',
          backgroundColor: 'rgba(255,77,79,0.10)',
          borderColor: 'rgba(255,77,79,0.40)',
          borderWidth: 1,
          label: {
            display: true,
            content: 'Incidente schema mismatch (jun-jul 2025)',
            position: { x: 'center', y: 'start' },
            color: '#cf1322',
          },
        },
      },
    },
  },
};

function AnnotationTest() {
  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold mb-2">chartjs-plugin-annotation — test view</h1>
      <p className="text-gray-500 mb-4">
        Validates annotation overlays (reference line + shaded band) on the
        existing chart.js@4 stack. Plugin scoped per-chart; safe to delete.
      </p>

      <Card title="SLA freshness — red reference line (annotation: line)">
        <div style={{ height: 280 }}>
          <Bar data={slaData} options={slaOptions} />
        </div>
      </Card>

      <Divider />

      <Card title="HMS incident timeline — shaded band (annotation: box)">
        <div style={{ height: 320 }}>
          <Line data={incidentData} options={incidentOptions} />
        </div>
      </Card>
    </div>
  );
}

export default AnnotationTest;
